import { select, put, call, fork, cancelled, delay, take, actionChannel, cancel, race, } from "@redux-saga/core/effects";
import { registerRequest, registerFailure, registerSuccess, createEndpointRequest, createEndpointSuccess, createEndpointFailure, connectToMqttRequest, connectToMqttSuccess, connectToMqttFailure, subscribeToMqttTopicFailure, subscribeToMqttTopicRequest, subscribeToMqttTopicSuccess, destroyEndpointRequest, destroyEndpointSuccess, destroyEndpointFailure, unregisterFailure, unregisterSuccess, disconnectFromMqttRequest, disconnectFromMqttSuccess, disconnectFromMqttFailure, unsubscribeFromMqttTopicRequest, unsubscribeFromMqttTopicSuccess, unsubscribeFromMqttTopicFailure, setWasPreviouslyConnectedToMqtt, clearAllStreamTasks, killAllAction, setBrowserAction, setStatsAction, } from "./actions";
import { shouldBeRetried, KEEPALIVE_SECONDS, retryIf, detectBrowser, } from "../Common";
import { identity as getIdentity, endpoint as getEndpoint, wasPreviouslyConnectedToMqtt as getWasPreviouslyConnectedToMqtt, state, } from "./selectors";
import log from "../log";
import { clearAllCalls } from "./call/actions";
import { buffers } from "redux-saga";
import { keepAliveReminderPattern } from "./patterns";
import { AuviousRtcState, } from "./model";
function* createEndpointSaga(context, registerOptions) {
    const userEndpointResource = context.getUserEndpointResource();
    const identity = context.getCommonClient().auth.token.userId;
    const serverKeepAliveReminderOn = !!registerOptions.serverKeepAliveReminderOn;
    try {
        yield put(createEndpointRequest(null));
        const endpoint = yield* retryIf(3, 1700, shouldBeRetried, [userEndpointResource, userEndpointResource.create], {
            userId: identity,
            keepAliveSeconds: registerOptions.keepAliveSeconds || KEEPALIVE_SECONDS,
            serverKeepAliveReminderOn,
        });
        yield put(createEndpointSuccess({
            identity,
            endpoint,
        }));
    }
    catch (error) {
        if (error instanceof Error) {
            yield put(createEndpointFailure({ message: error.message }));
        }
        else {
            yield put(createEndpointFailure({ message: "unknown reason" }));
        }
        throw error;
    }
    finally {
        if (yield cancelled()) {
            yield put(createEndpointFailure({ message: "cancelled" }));
        }
    }
}
function* destroyEndpointSaga(context) {
    const userEndpointResource = context.getUserEndpointResource();
    const identity = yield select(getIdentity);
    const endpoint = yield select(getEndpoint);
    try {
        yield put(destroyEndpointRequest(null));
        yield* retryIf(3, 1700, shouldBeRetried, [userEndpointResource, userEndpointResource.create], {
            userId: identity,
            userEndpointId: endpoint,
        }, { urlPostfix: "unregister" });
        yield put(destroyEndpointSuccess(null));
    }
    catch (error) {
        if (error instanceof Error) {
            yield put(destroyEndpointFailure({ message: error.message }));
        }
        else {
            yield put(destroyEndpointFailure({ message: "unknown reason" }));
        }
        throw error;
    }
    finally {
        if (yield cancelled()) {
            yield put(destroyEndpointFailure({ message: "cancelled" }));
        }
    }
}
function* connectToMqttSaga(context) {
    const commonClient = context.getCommonClient();
    try {
        yield put(connectToMqttRequest(null));
        yield put(setWasPreviouslyConnectedToMqtt(yield call([commonClient.events, commonClient.events.connected])));
        if (!(yield call([commonClient.events, commonClient.events.connected]))) {
            yield call([commonClient.events, commonClient.events.connect]);
        }
        yield put(connectToMqttSuccess(null));
    }
    catch (error) {
        if (error instanceof Error) {
            yield put(connectToMqttFailure({ message: error.message }));
        }
        else {
            yield put(connectToMqttFailure({ message: "unknown reason" }));
        }
        throw error;
    }
    finally {
        if (yield cancelled()) {
            yield put(connectToMqttFailure({ message: "cancelled" }));
        }
    }
}
function* disconnectFromMqttSaga(context) {
    const commonClient = context.getCommonClient();
    try {
        yield put(disconnectFromMqttRequest(null));
        if (yield call([commonClient.events, commonClient.events.connected])) {
            yield call([commonClient.events, commonClient.events.disconnect]);
        }
        yield put(disconnectFromMqttSuccess(null));
    }
    catch (error) {
        if (error instanceof Error) {
            yield put(disconnectFromMqttFailure({ message: error.message }));
        }
        else {
            yield put(disconnectFromMqttFailure({ message: "unknown reason" }));
        }
        throw error;
    }
    finally {
        if (yield cancelled()) {
            yield put(disconnectFromMqttFailure({ message: "cancelled" }));
        }
    }
}
function* subscribeToMqttSaga(context) {
    const commonClient = context.getCommonClient();
    try {
        yield put(subscribeToMqttTopicRequest(null));
        yield call([commonClient.events, commonClient.events.subscribe], context.getEndpointTopic());
        yield put(subscribeToMqttTopicSuccess(null));
    }
    catch (error) {
        if (error instanceof Error) {
            yield put(subscribeToMqttTopicFailure({ message: error.message }));
        }
        else {
            yield put(subscribeToMqttTopicFailure({ message: "unknown reason" }));
        }
        throw error;
    }
    finally {
        if (yield cancelled()) {
            yield put(subscribeToMqttTopicFailure({ message: "cancelled" }));
        }
    }
}
function* unsubscribeFromMqttSaga(context) {
    const commonClient = context.getCommonClient();
    try {
        yield put(unsubscribeFromMqttTopicRequest(null));
        yield call([commonClient.events, commonClient.events.unsubscribe], context.getEndpointTopic());
        yield put(unsubscribeFromMqttTopicSuccess(null));
    }
    catch (error) {
        if (error instanceof Error) {
            yield put(unsubscribeFromMqttTopicFailure({ message: error.message }));
        }
        else {
            yield put(unsubscribeFromMqttTopicFailure({ message: "unknown reason" }));
        }
        throw error;
    }
    finally {
        if (yield cancelled()) {
            yield put(unsubscribeFromMqttTopicFailure({ message: "cancelled" }));
        }
    }
}
// get list of fields to keep from peer connection statistics
function* requestStatsListSaga(context) {
    const statsResource = context.getStatsResource();
    try {
        const [statsList] = yield* retryIf(2, 1700, shouldBeRetried, [statsResource, statsResource.get], { urlPostfix: "reports/default" });
        yield put(setStatsAction(statsList.report));
    }
    catch (error) {
        // will not save stats timeseries
        yield put(setStatsAction({}));
    }
}
/**
 * Get iceServers from client or authenticated from server with autorefresh before expire
 * @param context
 * @param options
 */
export function* resolveIceServers(context, options) {
    const job = { id: -1 };
    let iceServers;
    if (!options.iceServers || options.iceServers.length === 0) {
        try {
            const response = yield* retryIf(3, 1700, shouldBeRetried, [context.getIceResource(), context.getIceResource().get], { urlPostfix: "iceServers" });
            // @ts-expect-error
            iceServers = response.iceServers;
            // refresh ice servers after a reasonable amount of time
            if (response.ttl > 60) {
                job.id = window.setTimeout(() => {
                    context.runSaga(resolveIceServers, context, {});
                }, (response.ttl - Math.min(response.ttl / 10, 600)) * 1000);
            }
        }
        catch (error) {
            log.error("IceServers request failed with:", error);
        }
    }
    if ((yield select(state)) !== AuviousRtcState.UNREGISTERED) {
        yield put(registerSuccess({ iceServers: iceServers || options.iceServers }));
    }
    return job;
}
export function* registerRequestSaga(args) {
    const { context, options, resolve, reject } = args;
    context.setCommonClient(options.auviousCommonClient);
    if (options.eventHandlers) {
        context.setEventHandlers(options.eventHandlers);
    }
    yield put(registerRequest(null));
    context.sendEvent("connecting");
    let succeeded = false;
    let refreshJob;
    let keepAliveReminderChannel;
    try {
        yield call(createEndpointSaga, context, options);
        yield call(connectToMqttSaga, context);
        yield call(subscribeToMqttSaga, context);
        yield fork(requestStatsListSaga, context);
        refreshJob = yield call(resolveIceServers, context, options);
        const identity = yield select(getIdentity);
        const endpoint = yield select(getEndpoint);
        context.sendEvent("connected");
        context.sendEvent("registered", { identity, endpoint });
        resolve();
        succeeded = true;
        // set browser info
        yield put(setBrowserAction(detectBrowser()));
        // interval is keep alive seconds minus a percentage, let's say 25%
        const keepAliveInterval = Math.round(KEEPALIVE_SECONDS * 1000 * 0.75);
        // create a channel for KeepAliveReminder mqtt events for this endpoint
        keepAliveReminderChannel = yield actionChannel(keepAliveReminderPattern(), buffers.expanding());
        while (true) {
            if (!options.serverKeepAliveReminderOn) {
                yield context.getCommonClient().delayUsingSetTimeout(keepAliveInterval);
            }
            else {
                yield take(keepAliveReminderChannel);
            }
            try {
                // removed the retryIf as common client already has retry mechanism
                yield context
                    .getUserEndpointResource()
                    .create({ userEndpointId: endpoint }, { urlPostfix: "keepalive" });
            }
            catch (error) {
                yield put(clearAllStreamTasks(null));
                context.sendEvent("connectionError", new Error("keepalive/heartbeat failure"));
                throw error;
            }
        }
    }
    catch (error) {
        yield put(clearAllCalls(null));
        if (error instanceof Error) {
            yield put(registerFailure(error.message));
            yield call(reject, new Error(error.message));
        }
        else {
            yield put(registerFailure("unkown error"));
            yield call(reject, new Error("unknown error"));
        }
    }
    finally {
        if ((refreshJob === null || refreshJob === void 0 ? void 0 : refreshJob.id) >= 0) {
            clearTimeout(refreshJob.id);
        }
        yield put(killAllAction(null));
        if (!succeeded && (yield cancelled())) {
            yield put(registerFailure("cancelled"));
            yield call(reject, new Error("cancelled!"));
        }
        else if (succeeded) {
            context.sendEvent("unregistered");
            yield call(unregisterSaga, context);
        }
        if (keepAliveReminderChannel) {
            keepAliveReminderChannel.close();
        }
        context.unsetCommonClient();
    }
}
export function* unregisterSaga(context) {
    try {
        const wasPreviouslyConnectedToMqtt = yield select(getWasPreviouslyConnectedToMqtt);
        yield call(unsubscribeFromMqttSaga, context);
        if (!wasPreviouslyConnectedToMqtt) {
            yield call(disconnectFromMqttSaga, context);
        }
        yield call(destroyEndpointSaga, context);
        yield put(unregisterSuccess(null));
    }
    catch (error) {
        if (error instanceof Error) {
            yield put(unregisterFailure(error.message));
        }
        else {
            yield put(unregisterFailure("unkown error"));
        }
    }
    finally {
        if (yield cancelled()) {
            yield put(unregisterFailure("cancelled"));
        }
    }
}
export function* unregisterRequestSaga(args) {
    log.debug("unregisterRequestSaga()");
    const { context, resolve, reject } = args;
    const registerTask = context.getRegisterTask();
    if (registerTask) {
        log.debug("unregisterRequestSaga()... canceling!");
        yield cancel(registerTask);
    }
    const { success, failure, timeout } = yield race({
        success: take(unregisterSuccess),
        failure: take(unregisterFailure),
        timeout: delay(5000),
    });
    if (success) {
        resolve();
    }
    else if (failure) {
        reject(new Error("failed"));
    }
    else if (timeout) {
        reject(new Error("timeout"));
    }
    else {
        reject(new Error("unknown"));
    }
}
export * from "./conference/sagas";
//# sourceMappingURL=sagas.js.map